import React, { useState, useEffect } from 'react';
import { Tag, Plus, Search, Edit2, Trash2, Percent, Calculator } from 'lucide-react';
import { API_BASE_URL } from '../global';
import Modal from '../components/Modal';
import { showAlert, showConfirm } from '../components/AlertPopup';
import './FormPage.css';

export default function PriceTables() {
  const [tables, setTables] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  const [formData, setFormData] = useState({
    nome: '',
    desconto_porcentagem: ''
  });

  useEffect(() => {
    fetchTables();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchTables = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tabelas.php`);
      if (!response.ok) {
        console.error('HTTP Error:', response.status);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setTables(data);
      }
    } catch (e) {
      console.error('Erro ao buscar tabelas de preço:', e);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpenModal = (table = null) => {
    if (table) {
      setEditingId(table.id);
      setFormData({
        nome: table.nome || '',
        desconto_porcentagem: table.desconto_porcentagem !== undefined ? String(table.desconto_porcentagem) : '0'
      });
    } else {
      setEditingId(null);
      setFormData({
        nome: '',
        desconto_porcentagem: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome) {
      showAlert('Aviso', 'Informe o Nome da Tabela de Preço.');
      return;
    }

    const descNum = parseFloat(formData.desconto_porcentagem);
    if (isNaN(descNum) || descNum < 0 || descNum > 100) {
      showAlert('Aviso', 'A porcentagem de desconto deve ser um valor entre 0% e 100%.');
      return;
    }

    const method = editingId ? 'PUT' : 'POST';
    const payload = editingId ? { ...formData, id: editingId, desconto_porcentagem: descNum } : { ...formData, desconto_porcentagem: descNum };

    try {
      const response = await fetch(`${API_BASE_URL}/tabelas.php`, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const res = await response.json();
      if (res.success) {
        showAlert('Sucesso', editingId ? 'Tabela de preço atualizada com sucesso!' : 'Tabela de preço criada com sucesso!');
        handleCloseModal();
        fetchTables();
      } else {
        showAlert('Erro', res.error || 'Erro ao salvar tabela de preço');
      }
    } catch (err) {
      console.error(err);
      showAlert('Erro', 'Falha de comunicação com o servidor');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Deseja realmente remover esta tabela de preço?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/tabelas.php?id=${id}`, {
        method: 'DELETE'
      });
      const res = await response.json();
      if (res.success) {
        showAlert('Sucesso', 'Tabela de preço removida!');
        fetchTables();
      } else {
        showAlert('Erro', res.error || 'Erro ao deletar tabela');
      }
    } catch (err) {
      console.error(err);
      showAlert('Erro', 'Erro de conexão com o servidor');
    }
  };

  const filteredTables = tables.filter((t) =>
    (t.nome || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredTables.length / itemsPerPage) || 1;
  const paginatedTables = filteredTables.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Simulação para o Modal
  const previewDiscountNum = parseFloat(formData.desconto_porcentagem) || 0;
  const sampleBasePrice = 100.00;
  const sampleDiscountVal = sampleBasePrice * (previewDiscountNum / 100);
  const sampleFinalPrice = sampleBasePrice - sampleDiscountVal;

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Tag size={28} color="var(--primary-color)" /> Tabelas de Preço
          </h1>
          <p className="page-subtitle">Cadastre tabelas com descontos percentuais para clientes e categorias</p>
        </div>
        <button className="btn-primary" style={{ width: 'auto', padding: '0.75rem 1.25rem' }} onClick={() => handleOpenModal()}>
          <Plus size={18} /> Nova Tabela
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div className="search-bar" style={{ width: '100%', maxWidth: '380px' }}>
            <Search className="search-icon" size={18} />
            <input
              type="text"
              className="search-input"
              placeholder="Buscar tabela por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Cód</th>
                <th>Nome da Tabela</th>
                <th>Desconto (%)</th>
                <th>Simulação Exemplo (Base R$ 100)</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTables.length > 0 ? (
                paginatedTables.map((table) => {
                  const desc = Number(table.desconto_porcentagem || 0);
                  const simFinal = (100 - (100 * desc / 100)).toFixed(2);
                  return (
                    <tr key={table.id}>
                      <td>#{table.id}</td>
                      <td>
                        <div className="font-medium" style={{ color: 'var(--text-main)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Tag size={16} className="text-muted" /> {table.nome}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-success" style={{ fontSize: '0.85rem', padding: '0.3rem 0.6rem', fontWeight: '600' }}>
                          {desc.toFixed(2)}% OFF
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                          De <span style={{ textDecoration: 'line-through' }}>R$ 100,00</span> por <strong style={{ color: 'var(--success-color)' }}>R$ {simFinal}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon" onClick={() => handleOpenModal(table)} title="Editar Tabela">
                            <Edit2 size={16} />
                          </button>
                          <button className="btn-icon danger" onClick={() => handleDelete(table.id)} title="Excluir Tabela">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--text-muted)' }}>
                    {searchTerm ? 'Nenhuma tabela encontrada para a busca.' : 'Nenhuma tabela de preço cadastrada ainda.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* CONTROLES DE PAGINAÇÃO */}
        {filteredTables.length > 0 && (
          <div style={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Exibindo {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredTables.length)} de {filteredTables.length} tabelas
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className="btn-outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Anterior
              </button>
              <span style={{ fontSize: '0.85rem', fontWeight: '500', padding: '0 0.5rem', color: 'var(--text-main)' }}>
                Página {currentPage} de {totalPages}
              </span>
              <button
                className="btn-outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL CADASTRO / EDIÇÃO */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingId ? 'Editar Tabela de Preço' : 'Criar Nova Tabela de Preço'}
        maxWidth="540px"
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', padding: '0.25rem 0' }}>
          <div className="form-group">
            <label>Nome da Tabela de Preço <span className="text-danger">*</span></label>
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleInputChange}
              className="standard-input"
              required
              placeholder="Ex: Tabela Atacado 15% / Tabela Parceiros"
            />
          </div>

          <div className="form-group">
            <label>Porcentagem de Desconto (%) <span className="text-danger">*</span></label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                name="desconto_porcentagem"
                value={formData.desconto_porcentagem}
                onChange={handleInputChange}
                className="standard-input"
                required
                placeholder="Ex: 15.00"
                style={{ paddingRight: '2.5rem' }}
              />
              <Percent size={18} style={{ position: 'absolute', right: '12px', color: 'var(--text-muted)' }} />
            </div>
          </div>

          {/* SIMULAÇÃO EM TEMPO REAL NO MODAL */}
          <div style={{
            backgroundColor: 'var(--bg-color)',
            border: '1px solid var(--border-color)',
            borderRadius: '10px',
            padding: '1rem',
            marginTop: '0.25rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: '600', color: 'var(--primary-color)', marginBottom: '0.5rem' }}>
              <Calculator size={16} /> Simulação Prática do Desconto
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
              <span>Preço Original Exemplo:</span>
              <span>R$ {sampleBasePrice.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#10b981', marginBottom: '0.4rem' }}>
              <span>Desconto ({previewDiscountNum.toFixed(2)}%):</span>
              <span>- R$ {sampleDiscountVal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', fontWeight: '700', color: 'var(--text-main)', borderTop: '1px dashed var(--border-color)', paddingTop: '0.5rem' }}>
              <span>Preço Final para o Cliente:</span>
              <span style={{ color: 'var(--primary-color)' }}>R$ {sampleFinalPrice.toFixed(2)}</span>
            </div>
          </div>

          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.85rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" className="btn-outline" onClick={handleCloseModal}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.65rem 1.5rem' }}>
              {editingId ? 'Salvar Alterações' : 'Criar Tabela'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
